import type {
  ChangeEvent,
  ComponentType,
  FC,
  FocusEvent,
  InputHTMLAttributes,
} from "react";
import { useId, useState } from "react";
import { IMaskInput } from "react-imask";
import { toClassList } from "~/utils/classes";
import FieldMessage from "../FieldMessage";
import "./style.css";

// Cast to avoid IMaskInput's unwieldy union type while preserving runtime correctness
const MaskedInput = IMaskInput as ComponentType<
  InputHTMLAttributes<HTMLInputElement> & {
    mask: string | RegExp;
    definitions?: Record<string, RegExp>;
  }
>;

export type Props = {
  id?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  maxLength?: number;
  type?: string;
  name?: string;
  className?: string;
  required?: boolean;
  mask?: string | RegExp;
  definitions?: Record<string, RegExp>;
};

const Input: FC<Props> = ({
  id,
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  disabled = false,
  error,
  maxLength,
  type = "text",
  name,
  className = "",
  required,
  mask,
  definitions,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const [localLength, setLocalLength] = useState(
    (value ?? defaultValue ?? "").length,
  );
  const currentLength = value !== undefined ? value.length : localLength;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (value === undefined) {
      setLocalLength(e.target.value.length);
    }
    onChange?.(e);
  };

  const wrapperClasses = toClassList([
    "input-field",
    error ? "input-field--error" : "",
    disabled ? "input-field--disabled" : "",
    className,
  ]);

  const sharedInputProps: InputHTMLAttributes<HTMLInputElement> = {
    id: inputId,
    className: toClassList([
      "input-field__control",
      error ? "input-field__control--error" : "",
      disabled ? "input-field--disabled" : "",
    ]),
    type,
    name,
    placeholder,
    value,
    defaultValue,
    onChange: handleChange,
    onBlur,
    disabled,
    required,
    "aria-invalid": !!error,
    "aria-describedby": error
      ? `${inputId}-error`
      : maxLength && !mask
        ? `${inputId}-count`
        : undefined,
  };

  return (
    <div className={wrapperClasses}>
      {label && (
        <label
          className="input-field__label"
          htmlFor={inputId}
        >
          {label}
        </label>
      )}
      {mask ? (
        <MaskedInput
          {...sharedInputProps}
          mask={mask}
          definitions={definitions}
        />
      ) : (
        <input
          {...sharedInputProps}
          maxLength={maxLength}
        />
      )}
      {!mask && maxLength && (
        <span
          className="input-field__count"
          id={`${inputId}-count`}
        >
          {currentLength}/{maxLength}
        </span>
      )}
      {error && (
        <FieldMessage
          id={`${inputId}-error`}
          type="error"
        >
          {error}
        </FieldMessage>
      )}
    </div>
  );
};

export default Input;
